import type { Level } from "building-types/levels/levelTypes";
import { RoomEnclosure, RoomClass, RoomCondition } from "building-types/rooms/roomTypesEnum";
import type { RoomAttributes } from "building-types/rooms/staticRoomAttributes";
import { assertUnreachable } from "common/utils/assertUnreachable";

export const ROOM_BASE_NAME = "Room";

export function roomAttributesNew({
  roomType,
  roomClass = RoomClass.HOUSE,
  roomEnclosure = RoomEnclosure.CONDITIONED,
  name = ROOM_BASE_NAME,
  shortName = name,
  ...flags
}: Partial<RoomAttributes> & Pick<RoomAttributes, "roomType">): RoomAttributes {
  return {
    roomType,
    roomClass,
    roomEnclosure,
    name,
    shortName,
    ...flags,
  };
}

export const isInteriorRoomEnclosure: { [K in RoomEnclosure]?: boolean } = {
  [RoomEnclosure.CONDITIONED]: true,
  [RoomEnclosure.UNCONDITIONED]: true,
};

export const isWalledRoomEnclosure: { [K in RoomEnclosure]?: boolean } = {
  [RoomEnclosure.CONDITIONED]: true,
  [RoomEnclosure.UNCONDITIONED]: true,
  [RoomEnclosure.EXTERIOR]: true,
};

export const isExteriorRoomEnclosure: { [K in RoomEnclosure]?: boolean } = {
  [RoomEnclosure.EXTERIOR]: true,
};

export const isOpenRoomEnclosure: { [K in RoomEnclosure]?: boolean } = {
  [RoomEnclosure.OPEN]: true,
};

export function roomClassBaseOffset(roomClass: RoomClass, level: Level): number {
  const { levelDatumsGenerated } = level;
  const { garage, porch, grade, deck } = levelDatumsGenerated;

  switch (roomClass) {
    case RoomClass.HOUSE:
    case RoomClass.BASEMENT:
      return 0;
    case RoomClass.GARAGE:
      return garage;
    case RoomClass.PORCH:
      return porch;
    case RoomClass.DECK:
      return deck;
    case RoomClass.PATIO:
    case RoomClass.EXTERIOR:
      return grade;
    default:
      assertUnreachable(roomClass);
  }
}

export function roomEnclosureToCondition(roomEnclosure: RoomEnclosure): RoomCondition {
  return roomEnclosure === RoomEnclosure.CONDITIONED ? RoomCondition.CONDITIONED : RoomCondition.UNCONDITIONED;
}
